// BIN ranges for major card brands
const binRanges = {
  VISA: {
    pattern: /^4[0-9]{12}(?:[0-9]{3})?$/,
    ranges: [
      [400000, 499999], // Traditional range
      [404000, 404099], // Visa Electron
      [417500, 417599], // Visa Electron
      [424000, 424099], // Visa Electron
      [424100, 424199], // Visa Electron
      [428000, 428099], // Visa Electron
      [428200, 428299], // Visa Electron
      [428600, 428699], // Visa Electron
      [484400, 484499], // Visa Electron
      [400022, 400022], // Visa Vale Refeição
      [402010, 402010], // Visa Vale Alimentação
      [428001, 428001], // Visa Vale Refeição
    ],
  },
  MASTERCARD: {
    pattern: /^(5[1-5][0-9]{14}|2[2-7][0-9]{14})$/,
    ranges: [
      [510000, 559999], // Traditional range (51-55)
      [222100, 272099], // Expanded range (2221-2720)
    ],
  },
  AMEX: {
    pattern: /^3[47][0-9]{13}$/,
    ranges: [
      [340000, 349999],
      [370000, 379999],
    ],
  },
  ELO: {
    pattern:
      /^(4011(78|79)|431274|438935|451416|457393|457631|457632|504175|627780|636297|636368|650031|650032|650033|650035|650036|650037|650038|650039|650040|650041|650042|650043|650044|650045|650046|650047|650048|650049|650050|650051|650405|650406|650407|650408|650409|650485|650486|650487|650488|650489|650490|650491|650492|650493|650494|650495|650496|650497|650498|650499|651652|651653|651654|651655|651656|651657|655000|655001|655002|655003|655004|655005|655006|655007|655008|655009|655010|655011|655012|655013|655014|655015|655016|655017|655018|655019)\d{10,12}$/,
    ranges: [
      [401178, 401179],
      [431274, 431274],
      [438935, 438935],
      [451416, 451416],
      [457393, 457393],
      [457631, 457632],
      [504175, 504175],
      [627780, 627780],
      [636297, 636297],
      [636368, 636368],
      [650031, 650051],
      [650405, 650409],
      [650485, 650499],
      [651652, 651657],
      [655000, 655019],
    ],
  },
  HIPERCARD: {
    pattern: /^(606282|3841\d{2})\d{10}$/,
    ranges: [
      [384100, 384199], // Hipercard
      [606282, 606282], // Hipercard
    ],
  },
};

/**
 * Implements the Luhn algorithm for credit card number validation
 * @param cardNumber Credit card number as string
 * @returns boolean indicating if the card number passes the Luhn check
 */
const luhnCheck = (cardNumber: string): boolean => {
  const digits = cardNumber.split('').map(Number);
  let sum = 0;
  let isEven = false;

  // Loop from right to left
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits[i];

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Checks if a BIN falls within the valid ranges for a card brand
 * @param bin First 6 digits of card number
 * @param ranges Array of [start, end] ranges to check against
 * @returns boolean indicating if BIN is in valid range
 */
const isValidBinRange = (bin: number, ranges: number[][]): boolean => {
  const binNum = parseInt(bin.toString().substring(0, 6));
  return ranges.some(([start, end]) => binNum >= start && binNum <= end);
};

/**
 * Validates a credit card number and identifies its brand
 * @param nr Credit card number as string
 * @returns Brand enum value if valid, false if invalid
 */
export const testarCC = (nr: string): boolean => {
  // Remove any non-digit characters
  const cardNumber = nr.replace(/\D/g, '');

  // Basic length check
  if (cardNumber.length < 13 || cardNumber.length > 19) {
    return false;
  }

  // Luhn algorithm check
  if (!luhnCheck(cardNumber)) {
    return false;
  }

  // Check against each card brand's pattern and BIN ranges
  for (const [, { pattern, ranges }] of Object.entries(binRanges)) {
    if (cardNumber.match(pattern) && isValidBinRange(parseInt(cardNumber.substring(0, 6)), ranges)) {
      return true;
    }
  }

  return false;
};

/**
 * Gets the BIN (first 6 digits) of a credit card number
 * @param cardNumber Credit card number as string
 * @returns BIN as string
 */
export const getBIN = (cardNumber: string): string => {
  return cardNumber.replace(/\D/g, '').substring(0, 6);
};

/**
 * Gets the last 4 digits of a credit card number
 * @param cardNumber Credit card number as string
 * @returns Last 4 digits as string
 */
export const getLastFourDigits = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\D/g, '');
  return cleaned.substring(cleaned.length - 4);
};
